import React, { useState } from 'react';
import { 
  profile_roadmaps_tabs_subscribed,
  profile_roadmaps_tabs_created,
  profile_error_roadmapLoad,
  profile_roadmaps_empty_mySubscribed,
  profile_roadmaps_empty_userSubscribed,
  profile_roadmaps_empty_myCreated,
  profile_roadmaps_empty_userCreated
} from '@paraglide/messages';
import styled from '@emotion/styled';
import { TabNav } from "@edusense/shared-ui-layout";
// Converted to direct ESM imports to resolve Vite bundling and initialization issues
import LucideMap from 'lucide-react/dist/esm/icons/map';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2';
import Inbox from 'lucide-react/dist/esm/icons/inbox';
import { RoadmapList } from "@edusense/roadmap-ui";
import { RoadmapFilterType, useRoadmapData } from '@edusense/features-course-hooks';
import { css } from '@emotion/react';

export interface TabItem {
  id: string;
  label: string;
}

interface ProfileRoadmapsTabProps {
  userId: string;
  isMyProfile?: boolean;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

type RoadmapSubTabType = 'subscribed_by_user' | 'created_by_user';

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
  color: var(--theme-text-secondary);
  border: 1px dashed var(--theme-ui-colors-gray3, #e5e7eb);
  border-radius: 0.5rem;
  margin-top: 1rem;
`;

const MapIcon = styled(LucideMap)`
  width: 3rem;
  height: 3rem;
  opacity: 0.5;
`;

const IconWrapper = styled(Inbox)`
  width: 3rem;
  height: 3rem;
  opacity: 0.5;
`;

const CenteredLoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 0;
`;

const TabsContainer = styled.div`
  margin-bottom: 1rem;
  margin-left: 1rem;
  margin-right: 1rem;
  margin-top: 0.5rem;
  position: relative;
  z-index: 5;
`;

const ProfileRoadmapsTab: React.FC<ProfileRoadmapsTabProps> = ({
  userId,
  isMyProfile = false,
  onView,
  onEdit,
  onDelete
}) => {
  
  const [activeSubTab, setActiveSubTab] = useState<RoadmapSubTabType>(
    isMyProfile ? 'created_by_user' : 'subscribed_by_user'
  );

  const subTabs: TabItem[] = [
    { id: 'subscribed_by_user', label: profile_roadmaps_tabs_subscribed() },
    { id: 'created_by_user', label: profile_roadmaps_tabs_created() },
  ];

  const {
    data: roadmaps,
    isLoading,
    isFetchingNextPage,
    error,
    hasNextPage,
    fetchNextPage,
    observerRef
  } = useRoadmapData({
    type: activeSubTab as RoadmapFilterType,
    userId,
    enabled: !!userId
  });

  console.log('[ProfileRoadmapsTab] Render:', { userId, isMyProfile, activeSubTab });

  return (
    <div>
      <TabsContainer>
        <TabNav
          tabs={subTabs}
          activeTabId={activeSubTab}
          onTabChange={(tabId) => setActiveSubTab(tabId as RoadmapSubTabType)}
          variant="indicator"
        />
      </TabsContainer>

      {isLoading && roadmaps.length === 0 && (
        <CenteredLoaderContainer>
          <Loader2 css={css`width: 2rem; height: 2rem; animation: spin 1s linear infinite; color: var(--theme-primary);`} />
        </CenteredLoaderContainer>
      )}

      {!isLoading && error && (
        <EmptyStateContainer>
          <p>{profile_error_roadmapLoad()}</p>
        </EmptyStateContainer>
      )}

      {!isLoading && !error && roadmaps.length === 0 && (
        <EmptyStateContainer>
          <IconWrapper />
          <p>
            {activeSubTab === 'subscribed_by_user'
              ? (isMyProfile 
                  ? profile_roadmaps_empty_mySubscribed() 
                  : profile_roadmaps_empty_userSubscribed())
              : (isMyProfile 
                  ? profile_roadmaps_empty_myCreated() 
                  : profile_roadmaps_empty_userCreated())
            }
          </p>
        </EmptyStateContainer>
      )}

      {!isLoading && !error && roadmaps.length > 0 && (
        <RoadmapList
          roadmaps={roadmaps}
          onRoadmapClick={onView}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          error={error}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          observerRef={observerRef}
          emptyStateContent={null}
        />
      )}
    </div>
  );
};

export default ProfileRoadmapsTab; 